import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Play, BookOpen, User, CheckCircle, Clock, Lock } from 'lucide-react';
import ProgressService from '../services/ProgressService';

const API_BASE_URL = '/api';
const ASSETS_URL = '';

const LandingPage = ({ onOpenAppMenu, user, onOpenAuth }) => {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [navigationData, setNavigationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userProgress, setUserProgress] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch both lessons and navigation data
        const [lessonsResponse, navigationResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/cards/lessons`),
          fetch(`${API_BASE_URL}/cards/navigation`)
        ]);

        if (lessonsResponse.ok) {
          const data = await lessonsResponse.json();
          setLessons(data);
        }

        if (navigationResponse.ok) {
          const navData = await navigationResponse.json();
          setNavigationData(navData);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Load user progress from localStorage for now
    const savedProgress = localStorage.getItem('user_progress');
    if (savedProgress) {
      setUserProgress(JSON.parse(savedProgress));
    }
  }, []);

  // Helper function to check sublesson completion
  const getSubLessonCompletion = (subLesson) => {
    if (!subLesson.children || subLesson.children.length === 0) {
      return { isCompleted: false, percentage: 0 };
    }

    let totalExercises = 0;
    let completedExercises = 0;

    for (const exercise of subLesson.children) {
      if (exercise.type === 'exercise' && exercise.cardType === 'exercise') {
        totalExercises++;
        if (ProgressService.isExerciseCompleted(exercise.id)) {
          completedExercises++;
        }
      }
    }

    const percentage = totalExercises > 0 ? Math.round((completedExercises / totalExercises) * 100) : 0;
    return {
      isCompleted: percentage === 100,
      percentage
    };
  };

  // Find next incomplete sublesson
  const getNextIncompleteSubLesson = () => {
    const findFirstIncomplete = (items) => {
      for (const item of items) {
        if (item.type === 'subLesson') {
          // Check if this sublesson is completed using the same logic as progress calculation
          const subLessonCompletion = getSubLessonCompletion(item);
          
          // If this sublesson is NOT completed, return it
          if (!subLessonCompletion.isCompleted) {
            return item.id;
          }
        } else if (item.children && item.children.length > 0) {
          const result = findFirstIncomplete(item.children);
          if (result) return result;
        }
      }
      return null;
    };

    return findFirstIncomplete(navigationData);
  };

  // Get the first card ID of a lesson dynamically
  const getFirstCardOfLesson = (lessonId) => {
    const lesson = navigationData.find(item => item.id === lessonId);
    if (lesson && lesson.children && lesson.children.length > 0) {
      return lesson.children[0].id;
    }
    return 7; // fallback
  };

  const getProgressStats = () => {
    const totalLessons = lessons.length;
    const completedLessons = Object.values(userProgress).filter(progress => progress.completed).length;
    const remainingLessons = totalLessons - completedLessons;
    
    return { totalLessons, completedLessons, remainingLessons };
  };

  const getLessonStatus = (lessonId, index) => {
    const progress = userProgress[lessonId];
    
    // First lesson is always available
    if (index === 0) {
      if (progress?.completed) return 'completed';
      if (progress?.started) return 'in-progress';
      return 'available';
    }
    
    // Other lessons unlock when previous is completed
    const prevLessonId = lessons[index - 1]?.lessonId;
    const prevProgress = userProgress[prevLessonId];
    
    if (!prevProgress?.completed) return 'locked';
    if (progress?.completed) return 'completed';
    if (progress?.started) return 'in-progress';
    return 'available';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} className="text-green-600" />;
      case 'in-progress':
        return <Clock size={16} className="text-orange-600" />;
      case 'locked':
        return <Lock size={16} className="text-gray-400" />;
      default:
        return <BookOpen size={16} className="text-blue-600" />;
    }
  };

  const handleStartCourse = () => {
    if (navigationData.length === 0) {
      // Fallback if navigation data not loaded yet
      navigate('/menu');
      return;
    }

    // Use the new logic to find first incomplete sublesson
    const nextIncompleteSubLesson = getNextIncompleteSubLesson();
    
    if (nextIncompleteSubLesson) {
      navigate(`/card/${nextIncompleteSubLesson}`);
    } else {
      // If no incomplete sublessons, go to first lesson dynamically
      const firstLessonCard = getFirstCardOfLesson(lessons[0]?.lessonId);
      navigate(`/card/${firstLessonCard}`);
    }
  };

  const stats = getProgressStats();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 font-['HelveticaRegular'] flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Left side: Menu and Logo */}
            <div className="flex items-center gap-2 sm:gap-4">
              <button
                onClick={onOpenAppMenu}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                aria-label="Open menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div className="flex items-center gap-2">
                <BookOpen className="text-blue-600 flex-shrink-0" size={24} />
                <h1 className="text-base sm:text-xl font-bold text-gray-900 font-['bpg-caps'] whitespace-nowrap">
                  აფხაზური ენის კურსი
                </h1>
              </div>
            </div>

            {/* Right side: User */}
            <button
              onClick={onOpenAuth}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-['bpg-caps'] flex-shrink-0 text-sm sm:text-base"
            >
              <User size={18} />
              <span className="hidden sm:inline">{user ? user.name : 'შესვლა'}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Content area with decorative images */}
      <div className="flex-1 relative overflow-hidden">
        {/* Decorative Images */}
        {/* Clock Tower with Palm - Bottom Left */}
        <div className="absolute bottom-0 -left-20 lg:-left-32 pointer-events-none z-0">
          <img 
            src={`${ASSETS_URL}/assets/images/clock-tower.png`}
            alt=""
            className="w-[500px] md:w-[600px] lg:w-[700px] opacity-90"
            style={{ maxHeight: '85vh' }}
          />
        </div>
        
        {/* Palm Tree - Top Right (starts exactly at top of this container = bottom of header) */}
        <div className="absolute top-0 right-0 pointer-events-none z-0">
          <img 
            src={`${ASSETS_URL}/assets/images/palm-right.png`}
            alt=""
            className="w-48 md:w-64 lg:w-80 opacity-90"
          />
        </div>

        {/* Partner Logos - Bottom Right */}
        <div className="absolute bottom-2 right-2 sm:bottom-4 sm:right-4 flex items-center gap-1 sm:gap-2 md:gap-3 z-10">
          <img 
            src={`${ASSETS_URL}/assets/images/logo-srnsfg.png`}
            alt="SRNSFG"
            className="h-10 sm:h-14 md:h-20 lg:h-24"
          />
          <img 
            src={`${ASSETS_URL}/assets/images/logo-enagramm.png`}
            alt="Enagramm"
            className="h-10 sm:h-14 md:h-20 lg:h-24"
          />
          <img 
            src={`${ASSETS_URL}/assets/images/logo-tsu.png`}
            alt="Tbilisi State University"
            className="h-10 sm:h-14 md:h-20 lg:h-24"
          />
        </div>

        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4 font-['bpg-caps']">
            კეთილი იყოს თქვენი მობრძანება!
          </h2>
          <p className="text-xl text-gray-600 mb-6 font-['HelveticaRegular']">
            ისწავლეთ აფხაზური ენა ინტერაქტიული გაკვეთილებითა და სავარჯიშოებით
          </p>
          
          {/* Progress Overview */}
          {!loading && lessons.length > 0 && user && (
            <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 font-['bpg-caps']">
                თქვენი პროგრესი
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 font-['HelveticaRegular']">
                    {getProgressStats().completedLessons}
                  </div>
                  <div className="text-sm text-gray-500">დასრულებული</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600 font-['HelveticaRegular']">
                    {getProgressStats().remainingLessons}
                  </div>
                  <div className="text-sm text-gray-500">დარჩენილი</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 font-['HelveticaRegular']">
                    {getProgressStats().totalLessons}
                  </div>
                  <div className="text-sm text-gray-500">სულ გაკვეთილი</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleStartCourse}
            disabled={loading || lessons.length === 0}
            className="flex items-center justify-center gap-2 px-8 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed font-['bpg-caps']"
          >
            <Play size={20} />
            კურსის დაწყება
          </button>
          
          <button
            onClick={() => navigate('/menu')}
            disabled={loading}
            className="flex items-center justify-center gap-2 px-8 py-4 bg-white text-blue-600 border-2 border-blue-600 rounded-xl hover:bg-blue-50 transition-colors text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed font-['bpg-caps']"
          >
            <BookOpen size={20} />
            ყველა გაკვეთილი
          </button>
        </div>

        {/* Course Description */}
        <div className="mt-12 text-center">
          <div className="max-w-2xl mx-auto bg-white/80 backdrop-blur-sm rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 font-['bpg-caps']">
              კურსის შესახებ
            </h3>
            <p className="text-gray-600 leading-relaxed">
              კურსი განკუთვნილია ნებისმიერი პირისთვის, ვისაც აფხაზურის სწავლის 
              სურვილი აქვს.{' '}
              <Link 
                to="/about"
                className="text-blue-600 hover:text-blue-800 transition-colors font-semibold font-['bpg-caps'] hover:underline"
              >
                უფრო ვრცლად კურსის შესახებ
              </Link>
            </p>
          </div>
        </div>
        </main>
      </div>
    </div>
  );
};

export default LandingPage;
